import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import SectionTitle from '../../Text/SectionTitle';

type EducationalBackgroundCampusSectionProps = {
  prefix: string;
  optionalFields?: string[];
  fieldNames?: {
    highSchool: string;
    countryHighSchool: string;
    regionHighSchool: string;
    communeHighSchool: string;
    typeHighSchool: string;
    graduationYear: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEducationalBackgroundCampus = ({
  prefix,
  fieldNames = {
    highSchool: 'highSchool',
    countryHighSchool: 'countryHighSchool',
    regionHighSchool: 'regionHighSchool',
    communeHighSchool: 'communeHighSchool',
    typeHighSchool: 'typeHighSchool',
    graduationYear: 'graduationYear',
  },
  disabled = false,
}: EducationalBackgroundCampusSectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();

  const [ownSustainer, selectedRegion] = watch([
    'ownSustainer',
    fieldNames.regionHighSchool,
  ]);

  const isRegionTouched = formState.touchedFields[fieldNames.regionHighSchool];

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.communeHighSchool, null);
    }
  }, [selectedRegion, setValue, isRegionTouched, fieldNames.communeHighSchool]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.highSchool}
              label={t(`${prefix}.${fieldNames.highSchool}`)}
              control={control}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.countryHighSchool}
              label={t(`${prefix}.${fieldNames.countryHighSchool}`)}
              options={[]}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.regionHighSchool}
              label={t(`${prefix}.${fieldNames.regionHighSchool}`)}
              options={[]}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.communeHighSchool}
              label={t(`${prefix}.${fieldNames.communeHighSchool}`)}
              options={[]}
              control={control}
              shouldUnregister
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.typeHighSchool}
              label={t(`${prefix}.${fieldNames.typeHighSchool}`)}
              options={[]}
              control={control}
              disabled={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.graduationYear}
              label={t(`${prefix}.${fieldNames.graduationYear}`)}
              control={control}
              disabled={disabled}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.countryHighSchool,
    fieldNames.communeHighSchool,
    fieldNames.regionHighSchool,
    fieldNames.typeHighSchool,
    fieldNames.graduationYear,
    fieldNames.highSchool,
    disabled,
    t,
    ownSustainer,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicEducationalBackgroundCampus;
